import React, { useCallback, useMemo } from 'react';
import _ from 'lodash';

import Conditional from '../../../common/conditional';
import MultiSelect from '../../../themed/multiselect';
import Select from '../../../themed/select/select';
import TextArea from '../../../common/textarea';

import { ChecklistItem } from '../../../../utils/models';
import { matchPhrases, validateAnomalyDistribution } from '../../../../utils/utilities';
import { MappedAnomalyOption } from '../../../../hooks/useMappedAnomalies';

import classNames from './style.module.scss';
import toastService from '../../../../services/toastService';

interface ChecklistFormProps {
	enableChecklistItemComment: boolean;
	label: string;
	lineNumber: string;
	anomalyItems: MappedAnomalyOption[];
	additionalComment: string;
	items: ChecklistItem[];
	onAddComment: (comment: string) => void;
	onItemChange: (items: ChecklistItem[]) => void;
}

const ChecklistForm: React.FC<ChecklistFormProps> = ({
	enableChecklistItemComment,
	label,
	lineNumber,
	anomalyItems,
	additionalComment,
	items,
	onAddComment,
	onItemChange,
}) => {

	const availableAnomalies = useMemo(() => {
		const selectedAnomalies = items.map((item) => item.anomalyItems).flat();
		const totalAnomalies = anomalyItems.map((anomaly) => ({ id: anomaly.anomalyId, name: `${anomaly.name}` }))

		return _.differenceBy(totalAnomalies, selectedAnomalies, 'id').map((option) => ({ id: option.id, name: `${option.name}` }));
	}, [anomalyItems, items]);

	const validateChecklistItem = useCallback((item: ChecklistItem) => {
		if (item.anomalyItems.length === 0) {
			return false;
		}

		if (enableChecklistItemComment) {
			const anomalyInvalidWithCommentCheck = items.some((c) => {
				return c.id !== item.id && (c.assessment === 'anomaly' && (c.anomalyItems.length === 0 || c.comment.length === 0));
			});

			return anomalyInvalidWithCommentCheck;
		}

		const anomalyInvalidWithoutCommentCheck = items.some((c) => {
			return c.id !== item.id && (c.assessment === 'anomaly' && (c.anomalyItems.length === 0));
		});

		return anomalyInvalidWithoutCommentCheck;
	}, [items, enableChecklistItemComment]);

	const getIsLastChecklistItem = useCallback((item: ChecklistItem) => {
		// if all other items have been filled except the current one
		const isLastItem = item.id === items[items.length - 1].id;
		if (isLastItem) return isLastItem;

		return items
			.filter((checklistItem) => checklistItem.id !== item.id)
			.every((item) => {
				const isAssessmentFilled = item.assessment.length > 0;
				if (isAssessmentFilled) {
					const isValid = matchPhrases(item.assessment, 'anomaly') && item.anomalyItems.length > 0;
					return isValid;
				}

				return false;
			});
	}, [items]);

 	const handleCommentChange = useCallback((item: ChecklistItem, comment: string) => {
		const newItems = items.map((pipe) =>
			pipe.id === item.id
				? {
						id: item.id,
						lineNumber: item.lineNumber,
						category: item.category,
						error: true,
						assessment: item.assessment,
						comment,
						anomalyItems: item.anomalyItems,
				  }
				: pipe
		);

		onItemChange(newItems);
	}, [items, onItemChange]);

 	const handleAssessmentSelect = useCallback((item: ChecklistItem, assessment: ChecklistItem['assessment']) => {
		if (validateChecklistItem(item)) {
			return toastService.error(`At least one checklist item not filled!`);
		}

		const newItems = items.map((pipe) =>
			pipe.id === item.id
				? {
						id: item.id,
						lineNumber: item.lineNumber,
						category: item.category,
						error: true,
						assessment,
						comment:  item?.comment?.length === 0 && assessment === 'ok' ? 'No anomaly.' : item?.comment,
						anomalyItems: [] as MappedAnomalyOption[],
				  }
				: pipe
		);

		if (getIsLastChecklistItem(item) &&  validateAnomalyDistribution(newItems, anomalyItems)) {
			return toastService.error('Please ensure all anomalies are captured in checklist!')
		}

		onItemChange(newItems);
	}, [items, anomalyItems, getIsLastChecklistItem, onItemChange, validateChecklistItem]);

	const handleAnomalyNumberSelect = useCallback((item: ChecklistItem, options: MappedAnomalyOption[]) => {
		const newItems = items.map((pipe) =>
			pipe.id === item.id
				? {
						id: item.id,
						lineNumber: item.lineNumber,
						assessment: item.assessment,
						category: item.category,
						anomalyItems: options,
						comment: item.comment,
						error: options.length === 0,
				  }
				: pipe
		);

		if (getIsLastChecklistItem(item) && validateAnomalyDistribution(newItems, anomalyItems)) {
			return toastService.error('Please ensure all anomalies are captured in checklist!')
		}

		onItemChange(newItems);
	}, [items, anomalyItems, getIsLastChecklistItem, onItemChange]);

	const renderChecklistItems = useCallback((itemsToRender: ChecklistItem[]) => {
		return itemsToRender
			.map((item) => (
				<tr key={item.id}>
					<td>{item.id}</td>
					<td>{item.lineNumber}</td>
					<td className='text-center'>
						<Select
							options={['Leak', 'Vibration'].includes(item.lineNumber) ? optionsWithoutNotApplicable : allOptions}
							name="value"
							placeholder="Select Assessment"
							value={item.assessment}
							onChange={(e) => handleAssessmentSelect(item, e.currentTarget.value as any)}
						/>
					</td>
					
					<Conditional isVisible={enableChecklistItemComment}>
						<td className='text-center'>
							<TextArea
								placeholder="Enter a comment"
								value={item.comment}
								onChange={(e) => handleCommentChange(item, e.currentTarget.value)}
								rows={3}
								error={(item.assessment === 'anomaly' && item?.comment?.length === 0) ? 'Please enter a comment' : ''}
							/>
						</td>
					</Conditional>

					<td className='text-center'>
						{item.assessment === 'anomaly' ? (
							<MultiSelect
								options={availableAnomalies}
								showCheckbox={false}
								singleSelect={false}
								loading={false}
								label=""
								error={(item.assessment === 'anomaly' && item.anomalyItems.length === 0) ? 'Please select an anomaly' : ''}
								selectedValues={item.anomalyItems}
								onSearch={() => {}}
								onSelect={(options) => handleAnomalyNumberSelect(item, options)}
								onRemove={(options) => handleAnomalyNumberSelect(item, options)}
								placeholder="Select Anomaly Items"
							/>
						) : (
							'N/A'
						)}
					</td>
				</tr>
			));
	}, [availableAnomalies, enableChecklistItemComment, handleAnomalyNumberSelect, handleCommentChange, handleAssessmentSelect]);

	const renderGroupedChecklistItems = useCallback(() => {
		const groupedItems = _.groupBy(items, 'category');

		return (
			<>
				{Object.keys(groupedItems).map(key => {
					return (
						<Conditional key={key} isVisible={groupedItems[key].length > 0}>
							<tr>
								<td className={classNames.checklistTableCaption} colSpan={5}>
									{key.split('-').join(' ')}
								</td>
							</tr>
	
							{renderChecklistItems(groupedItems[key])}
						</Conditional>
					)
				})}
			</>
		)
	}, [items, renderChecklistItems]);

	return (
		<>
			<table className={classNames.checklistTable}>
				<thead>
					<tr>
						<th rowSpan={2}>Item</th>
						<th rowSpan={2} className={classNames.checklistTableTenderBlueBG}>
							{label}: {lineNumber}
						</th>
						<th colSpan={3}>Assessment</th>
					</tr>
					<tr>
						<th>Condition</th>
						<Conditional isVisible={enableChecklistItemComment}>
							<th>Comment</th>
						</Conditional>
						<th>Anomaly No</th>
					</tr>
				</thead>
				<tbody>
					{renderGroupedChecklistItems()}
				</tbody>
			</table>

			<div className={classNames.checklistTableComment}>
				<div className={classNames.checklistTableCommentHeader}>
					Additional comment, if any
				</div>
				<div className={classNames.checklistTableCommentBody}>
					<TextArea
						rows={6}
						value={additionalComment}
						onChange={(e) => onAddComment(e.currentTarget.value)}
					/>
				</div>
			</div>
		</>
	);
};

const allOptions = [
	{
		id: 'ok',
		name: 'OK',
	},
	{
		id: 'anomaly',
		name: 'ANOMALY',
	},
	{
		id: 'na',
		name: 'N/A',
	},
];

const optionsWithoutNotApplicable = [
	{
		id: 'ok',
		name: 'OK',
	},
	{
		id: 'anomaly',
		name: 'ANOMALY',
	},
];

export default ChecklistForm;
